import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useId, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';

interface ReasonedButtonProps {
  /** Why it cannot be used now, or null when it can. */
  reason: string | null;
  /** Off for another cause too, such as staff viewing or a save on its way. */
  disabled?: boolean;
  /** Not needed by a button that is always off. */
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  testId: string;
  children: ReactNode;
}

/**
 * A button that a limit can switch off, saying why: the reason is its
 * tooltip and its accessible description, so a screen reader that finds the
 * disabled button hears it too (arc-and-discovery-hardening, B16).
 *
 * A disabled button fires no pointer events, so the tooltip sits on a
 * wrapper around it.
 */
export function ReasonedButton({
  reason,
  disabled = false,
  onClick,
  variant,
  testId,
  children,
}: ReasonedButtonProps) {
  const reasonId = useId();
  if (!reason) {
    return (
      <Button
        variant={variant}
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
      >
        {children}
      </Button>
    );
  }
  return (
    <>
      <Tooltip title={reason} describeChild>
        <span>
          <Button
            variant={variant}
            disabled
            aria-describedby={reasonId}
            data-testid={testId}
          >
            {children}
          </Button>
        </span>
      </Tooltip>
      <Box component="span" id={reasonId} hidden data-testid={`${testId}-hint`}>
        {reason}
      </Box>
    </>
  );
}
